import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "cg_geo_consent_v1";
const COORDS_KEY = "cg_geo_coords_v1";

// Arrondi pour limiter la précision à ~500m (confidentialité RGPD)
// 1° latitude ≈ 111km. 0.005° ≈ 555m.
const PRECISION = 0.005;
const fuzz = (n: number) => Math.round(n / PRECISION) * PRECISION;

export type GeoConsentStatus = "unknown" | "granted" | "denied";

export interface FuzzyCoords {
  latitude: number;
  longitude: number;
  updatedAt: number;
}

interface StoredCoords extends FuzzyCoords {}

export const getStoredConsent = (): GeoConsentStatus => {
  if (typeof window === "undefined") return "unknown";
  return (localStorage.getItem(STORAGE_KEY) as GeoConsentStatus) || "unknown";
};

export const getStoredCoords = (): FuzzyCoords | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(COORDS_KEY);
    return raw ? (JSON.parse(raw) as StoredCoords) : null;
  } catch {
    return null;
  }
};

export const useGeolocation = () => {
  const [consent, setConsent] = useState<GeoConsentStatus>(getStoredConsent);
  const [coords, setCoords] = useState<FuzzyCoords | null>(getStoredCoords);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setConsent(getStoredConsent());
      if (e.key === COORDS_KEY) setCoords(getStoredCoords());
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const requestLocation = useCallback((): Promise<FuzzyCoords | null> => {
    return new Promise((resolve) => {
      if (!("geolocation" in navigator)) {
        setError("Géolocalisation non disponible sur cet appareil.");
        resolve(null);
        return;
      }
      setLoading(true);
      setError(null);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const fuzzy: FuzzyCoords = {
            latitude: fuzz(pos.coords.latitude),
            longitude: fuzz(pos.coords.longitude),
            updatedAt: Date.now(),
          };
          localStorage.setItem(STORAGE_KEY, "granted");
          localStorage.setItem(COORDS_KEY, JSON.stringify(fuzzy));
          setConsent("granted");
          setCoords(fuzzy);
          setLoading(false);
          resolve(fuzzy);
        },
        (err) => {
          setLoading(false);
          if (err.code === err.PERMISSION_DENIED) {
            localStorage.setItem(STORAGE_KEY, "denied");
            setConsent("denied");
            setError("Vous avez refusé l'accès à votre position.");
          } else {
            setError("Impossible de récupérer votre position.");
          }
          resolve(null);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 1000 * 60 * 30 },
      );
    });
  }, []);

  const refresh = useCallback(async () => {
    if (consent !== "granted") return null;
    return requestLocation();
  }, [consent, requestLocation]);

  const revoke = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(COORDS_KEY);
    setConsent("unknown");
    setCoords(null);
  }, []);

  const decline = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, "denied");
    setConsent("denied");
  }, []);

  return {
    consent,
    coords,
    loading,
    error,
    requestLocation,
    refresh,
    revoke,
    decline,
  };
};
